import { useValidClusterName } from '@app/components/NamesValid';
import { IconCluster } from '@app/components/icons/icon-cluster';
import { IconClusterWhite } from '@app/components/icons/icon-cluster-white';
import ButtonCustom from '@app/pages/Deployments/components/button';
import { checkUserRoleForOrg } from '@app/utils/checkUserRoles';
import {
  Alert,
  Button,
  Divider,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormSelect,
  FormSelectOption,
  Grid,
  GridItem,
  Modal,
  ModalVariant,
  Radio,
  Spinner,
  Text,
  TextInput,
  Title,
} from '@patternfly/react-core';
import { CheckCircleIcon, CheckIcon, ExclamationCircleIcon } from '@patternfly/react-icons';
import cs from 'classnames';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { API } from 'src/api';
import { GetClustersResponse, useGetRegions } from 'src/query/deployments';
import { OrganizationRepresentation } from 'src/query/orgs';

type Props = {
  isModalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  clusters: GetClustersResponse;
};

type SelectionProps = {
  id: string;
  title: string;
  isChecked: boolean;
  onChange?: () => void;
  children?: React.ReactNode;
};

export const CreateCluster: React.FC<Props> = ({ isModalOpen, setModalOpen, clusters }) => {
  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    reset,
  } = useForm();

  const { checkAvailabilty, nameValidityResponseText, clearNameValid } = useValidClusterName();
  const { isNameValid, nameHelperText } = nameValidityResponseText();

  const [orgs, setOrgs] = useState<OrganizationRepresentation[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<SelectionProps['id']>('annually');

  const getUserOrgs = async () => setOrgs(await API.User.getUserOrgs());

  const { data: regions = [] } = useGetRegions({
    queryParams: { shared: false },
    pathParams: { realm: 'self' },
  });

  useEffect(() => {
    if (isModalOpen) {
      getUserOrgs();
    }

    if (isModalOpen === false) {
      clearNameValid();
      reset();
    }
  }, [isModalOpen]);

  const handleModalToggle = () => {
    setModalOpen(!isModalOpen);
  };

  const submitForm = async (dep) => {
    try {
      toast('Creating cluster...');
      const pid = selectedPlan === 'annually' ? 'price_1NIF60HKDfkhAQNdvn91gBAe' : 'price_1NIF60HKDfkhAQNdIzVXFKsb';
      const createCluster = await API.Orgs.createCluster({
        name: dep.name,
        region: dep.region,
        domain: dep.domain,
        price_id: pid,
        org_id: dep.organization,
      });

      if (createCluster.ok) {
        toast.success(`Redirecting you to Stripe...`);
        setModalOpen(false);
        const redirectResp = await createCluster.json();
        return (window.location.href = redirectResp['link']);
      }
      const errorResp = await createCluster.json();
      toast.error(`${dep.name} cluster was not able to be created. (${errorResp.error})`);
    } catch (e) {
      console.log('[Error] Not able to create the cluster.');
    }
  };

  const manageRoleOrgs = orgs.filter((org) => checkUserRoleForOrg('manage-organization', org.id));

  const SelectionOption = ({ id, title, isChecked, onChange, children }: SelectionProps) => (
    <div
      onClick={onChange}
      style={{ cursor: 'pointer' }}
      className={cs('selection-option', {
        selected: isChecked,
      })}
    >
      <div className="selection-option__layout">
        {isChecked && (
          <div className="selection-option__check">
            <CheckCircleIcon />
          </div>
        )}
        <Radio
          id={id}
          name={'pricing'}
          onChange={onChange}
          isChecked={isChecked}
          title={title}
          aria-label={title}
          style={{ display: 'none' }}
        />
        <div className="selection-option__content">
          <Flex justifyContent={{ default: 'justifyContentSpaceBetween' }}>
            <Title headingLevel={'h3'}>{title}</Title>
            <FlexItem>{children}</FlexItem>
          </Flex>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <ButtonCustom
        content={
          <>
            <IconClusterWhite height="28px" width="28px" />
            <div>Create Dedicated Cluster</div>
          </>
        }
        onClick={handleModalToggle}
      />
      <Modal
        variant={ModalVariant.medium}
        isOpen={isModalOpen}
        onClose={handleModalToggle}
        title={
          <Flex alignItems={{ default: 'alignItemsCenter' }}>
            <IconCluster />
            <Title headingLevel={'h1'}>Enable Clusters</Title>
          </Flex>
        }
        actions={[
          <Button
            key="create"
            variant="primary"
            form="modal-with-form-create-cluster"
            type="submit"
            isDisabled={isSubmitting}
          >
            {isSubmitting ? (
              <div>
                <Spinner
                  isSVG
                  size="md"
                  aria-label="Contents of the small example"
                  style={{ '--pf-c-spinner--Color': 'white' }}
                />{' '}
                Creating cluster...
              </div>
            ) : (
              'Upgrade and enable clusters'
            )}
          </Button>,
          <Button key="cancel" variant="link" onClick={handleModalToggle}>
            Cancel
          </Button>,
        ]}
      >
        <Form id="modal-with-form-create-cluster" onSubmit={handleSubmit(submitForm)}>
          <Flex direction={{ default: 'column' }}>
            {clusters.length > 0 && (
              <Alert title="Existing Cluster(s)" variant="warning" isInline>
                You have existing clusters. You can add a deployment to an existing cluster. Additional clusters may
                still be wanted to split by organization or region. In that case, please continue.
              </Alert>
            )}
            <div>Get more powerful features like:</div>
            <Grid span={12}>
              {[
                { title: 'Scalability', descriptions: 'Dedicated resources to ensure availabilty and scale.' },
                {
                  title: 'Isolation',
                  descriptions: 'Database, compute and network are isolated from other customers.',
                },
              ].map((item, key) => (
                <GridItem span={6} key={key}>
                  <Flex>
                    <FlexItem>
                      <CheckIcon />
                    </FlexItem>
                    <FlexItem flex={{ default: 'flex_1' }}>
                      <Title headingLevel={'h3'}>{item.title}</Title>
                      <Text>{item.descriptions}</Text>
                    </FlexItem>
                  </Flex>
                </GridItem>
              ))}
            </Grid>
            <Divider />
            <Flex alignItems={{ default: 'alignItemsStretch' }}>
              <SelectionOption
                id="annually"
                title="Annually"
                isChecked={selectedPlan === 'annually'}
                onChange={() => {
                  setSelectedPlan('annually');
                }}
              >
                <div>$499 / month</div>
                <Text style={{ opacity: 0.5 }}>Billed $5,988.00 annually</Text>
              </SelectionOption>

              <SelectionOption
                id="monthly"
                title="Monthly"
                isChecked={selectedPlan === 'monthly'}
                onChange={() => {
                  setSelectedPlan('monthly');
                }}
              >
                <div>$749 / month</div>
              </SelectionOption>
            </Flex>
          </Flex>
          <Flex direction={{ default: 'column' }}>
            <FormGroup label="Region" isRequired fieldId="modal-with-form-form-region">
              <Controller
                name="region"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <FormSelect id="region-choose-form" aria-label="Choose a region" {...field}>
                    <FormSelectOption value="" label="Select one" isDisabled isPlaceholder />
                    {regions.map((option) => (
                      <FormSelectOption
                        key={option.region}
                        value={option.region}
                        label={`${option.provider?.toUpperCase()} - ${option.name} (${option.region})`}
                      />
                    ))}
                  </FormSelect>
                )}
              />
            </FormGroup>
            <FormGroup label="Organization" isRequired fieldId="modal-with-form-form-email">
              <Controller
                name="organization"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <FormSelect id="organization-choose-form" aria-label="Choose an organizaton" {...field}>
                    <FormSelectOption value="" label="Select one" isDisabled isPlaceholder />
                    {manageRoleOrgs.map((option, index) => (
                      <FormSelectOption
                        key={option.id}
                        value={option.id}
                        label={`${option.displayName} (${option.id})`}
                      />
                    ))}
                  </FormSelect>
                )}
              />
            </FormGroup>
            <FormGroup
              label="Name"
              isRequired
              fieldId="modal-with-form-form-name"
              validated={isNameValid}
              helperText={nameHelperText}
              helperTextInvalid={nameHelperText}
              helperTextInvalidIcon={<ExclamationCircleIcon />}
            >
              <Controller
                name="name"
                control={control}
                rules={{ required: true, min: 3 }}
                render={({ field }) => (
                  <TextInput
                    id="modal-with-form-form-name"
                    isRequired
                    type="text"
                    validated={isNameValid}
                    helperText={nameHelperText}
                    onKeyUp={(e) => checkAvailabilty(e.target.value)}
                    {...field}
                  />
                )}
              />
            </FormGroup>
            <FormGroup label="Domain name" fieldId="modal-with-form-form-name">
              <Controller
                name="domain"
                control={control}
                rules={{ min: 3 }}
                render={({ field }) => <TextInput id="modal-with-form-form-name" type="text" {...field} />}
              />
            </FormGroup>
            <Text>
              By creating a Cluster, you agree to be bound by the terms of our{' '}
              <a href="https://phasetwo.io/docs/service-agreement" target="_blank" rel="noreferrer">
                Service Agreement
              </a>{' '}
              and{' '}
              <a href="https://phasetwo.io/docs/sla" target="_blank" rel="noreferrer">
                SLA
              </a>
              . After clicking Upgrade, you will be sent to our payment partner, Stripe, to securely set up your account
              and payment method.
            </Text>
          </Flex>
        </Form>
      </Modal>
    </>
  );
};
